import React, { useCallback } from 'react';
import SavedSearchSelector from '../components/SavedSearchSelector';
import { useSavedSearch } from '../hooks/useSavedSearch';
import { SavedSearchManagerProps } from '../types';

function SavedSearchManager({
  tableType,
  tableState,
  initialFilters,
  onConfigApplied,
  variant = 'standard',
  style = {},
  className = '',
  profileType = 'SavedSearch',
  storageKeyPrefix = 'savedSearch-',
  configMode = 'table',
  defaultProfiles = []
}: SavedSearchManagerProps) {
  const initialConfig = tableState || {};
  const isOpaque = configMode === 'opaque';

  const {
    savedSearches,
    currentSearch,
    isLoading,
    error,
    successMessage,
    applySavedSearch,
    clearAllFilters,
    saveCurrentConfig,
    deleteSavedSearch,
    setAsDefault,
    removeAsDefault,
    updateCurrentConfig,
    shareSavedSearch,
    renameSavedSearch,
    hasUnsavedChanges
  } = useSavedSearch(tableType, initialConfig, onConfigApplied, initialFilters,
    { configMode, profileType, storageKeyPrefix, defaultProfiles });

  const isDefaultProfile = (currentSearch as any)?.isSyntheticDefault === true;

  // Track table state changes
  React.useEffect(() => {
    if (tableState) {
      updateCurrentConfig(tableState);
    }
  }, [tableState, updateCurrentConfig]);

  const handleApplySavedSearch = useCallback((searchId: string | null) => {
    if (!searchId) {
      const config = clearAllFilters();
      if (onConfigApplied) {
        onConfigApplied(config, false); // null in opaque mode
      }
      return;
    }
    const config = applySavedSearch(searchId);
    if (config && onConfigApplied) {
      onConfigApplied(config, false);
    }
  }, [applySavedSearch, clearAllFilters, onConfigApplied]);

  const handleSave = useCallback(async () => {
    const configToSave = tableState;
    return await saveCurrentConfig(null, 'private', configToSave);
  }, [tableState, saveCurrentConfig]);

  const handleSaveAs = useCallback(async (name: string, shareType: string) => {
    const configToSave = tableState;
    return await saveCurrentConfig(name, shareType, configToSave);
  }, [tableState, saveCurrentConfig]);

  const handleDelete = useCallback(async (searchId: string | number) => {
    const result = await deleteSavedSearch(searchId);
    if (isOpaque && result === null && onConfigApplied) {
      onConfigApplied(null, false);
    } else if (result && typeof result === 'object' && (result as any).filters && onConfigApplied) {
      onConfigApplied(result as any, false);
    }
    return result;
  }, [deleteSavedSearch, onConfigApplied, isOpaque]);

  return (
    <SavedSearchSelector
      savedSearches={savedSearches}
      currentSearch={currentSearch}
      currentConfig={tableState as any}
      hasUnsavedChanges={hasUnsavedChanges}
      isLoading={isLoading}
      error={error}
      successMessage={successMessage}
      onSelect={handleApplySavedSearch}
      onSave={handleSave}
      onSaveAs={handleSaveAs}
      onDelete={handleDelete}
      onRename={renameSavedSearch}
      onSetDefault={(id: string | number) => { setAsDefault(id); }}
      onRemoveDefault={(id: string | number) => { removeAsDefault(id); }}
      onShare={shareSavedSearch}
      variant={variant}
      className={className}
      style={style}
      configMode={configMode}
      isDefaultProfile={isDefaultProfile}
    />
  );
}

export default SavedSearchManager;
